/**
 * The working middle of a turn (Dunea Work's turn fold), shown as a step-by-step
 * timeline of the steps that matter: pages written, checks run, the preview
 * started. Background steps (reading files, planning, thinking) and the
 * narration between them stay out of the timeline — "Show all steps" opens the
 * full list. While building, the step in flight is lit; past steps are ticked.
 */
import { useState, type ReactNode } from 'react';
import type { ToolPart } from '~/components/chat/message-parts';
import type { Block } from '~/components/chat/fold-segments';
import WithTooltip from '~/components/ui/Tooltip';
import { plainStep, plainSummary, type PlainStep } from './plain-steps';
import { toolNameOf } from './tool-meta';
import { StepTooltip } from './ToolLine';
import { DuneaSpinner } from './DuneaSpinner';

const isPending = (p: ToolPart) => p.state !== 'output-available' && p.state !== 'output-error';

/** Steps a site owner would not miss: looking around, planning, saving notes. */
const BACKGROUND_TOOLS = new Set([
  'read_files',
  'list_files',
  'glob',
  'search_code',
  'task_manager',
  'remember',
  'get_user_uploads',
]);

/** At most this many timeline rows; the rest sit behind "Show all steps". */
export const TIMELINE_ROWS = 8;

export interface TimelineRow {
  key: string;
  step: PlainStep;
  count: number;
  running: boolean;
}

/** First line of a narration block, without markdown marks. */
export function narrationLine(text: string): string {
  const line =
    text
      .split('\n')
      .map((l) => l.trim())
      .find(Boolean) ?? '';

  return line.replace(/[*_`#>]+/g, '').trim();
}

/**
 * The important steps, in order: background tools dropped, repeats of the same
 * step merged ("Updated the home page ×3"). The step in flight is always shown,
 * even a background one, so the timeline never looks idle while work happens.
 * Capped to the latest TIMELINE_ROWS while building, the first ones once done.
 */
export function timelineRows(parts: ToolPart[], isStreaming: boolean): { rows: TimelineRow[]; hidden: number } {
  const rows: TimelineRow[] = [];

  for (const part of parts) {
    const running = isStreaming && isPending(part);

    if (BACKGROUND_TOOLS.has(toolNameOf(part)) && !running) {
      continue;
    }

    const step = plainStep(part, !running);
    const last = rows[rows.length - 1];

    if (last && last.step.text === step.text && !last.running) {
      last.count++;
      last.running = running;
      continue;
    }

    rows.push({ key: part.toolCallId, step, count: 1, running });
  }

  if (rows.length <= TIMELINE_ROWS) {
    return { rows, hidden: 0 };
  }

  return {
    rows: isStreaming ? rows.slice(-TIMELINE_ROWS) : rows.slice(0, TIMELINE_ROWS),
    hidden: rows.length - TIMELINE_ROWS,
  };
}

function TimelineItem({ row, isLast }: { row: TimelineRow; isLast: boolean }) {
  return (
    <li className="relative flex items-start gap-3 pb-2.5 last:pb-0">
      {!isLast && <span aria-hidden className="absolute left-[9.5px] top-[22px] bottom-0 w-px bg-hairline" />}
      <span
        className={`relative z-[1] mt-px flex h-[20px] w-[20px] flex-shrink-0 items-center justify-center rounded-full border ${
          row.running ? 'border-accent/50 bg-accent/10' : 'border-hairline bg-surface'
        }`}
      >
        {row.running ? (
          <DuneaSpinner size={11} className="text-accent" />
        ) : (
          <span className={`${row.step.icon} text-[11px] text-muted`} />
        )}
      </span>
      <WithTooltip
        tooltip={<StepTooltip explain={row.step.explain} tech={row.step.tech} />}
        position="top"
        delay={300}
        maxWidth={320}
      >
        <span
          className={`min-w-0 pt-[1px] text-[13.5px] leading-[1.45] cursor-default ${
            row.running ? 'text-primary' : 'text-secondary'
          }`}
        >
          {row.step.text}
          {row.count > 1 && <span className="ml-1.5 text-[12px] text-faint">×{row.count}</span>}
        </span>
      </WithTooltip>
    </li>
  );
}

export function WorkedBlock({
  blocks,
  parts,
  isStreaming = false,
  renderBlock,
}: {
  blocks: Block[];
  parts: ToolPart[];
  isStreaming?: boolean;
  renderBlock: (block: Block) => ReactNode;
}) {
  const [open, setOpen] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const summary = plainSummary(parts);
  const { rows, hidden } = timelineRows(parts, isStreaming);
  const total = parts.length;
  // The model's latest narration line ("Verifying the build now.") — shown in the
  // box while building, so the live state reads as one place, not loose text.
  const lastText = isStreaming ? [...blocks].reverse().find((b) => b.kind === 'text') : undefined;
  const now = lastText && lastText.kind === 'text' ? narrationLine(lastText.text.split(/\n{2,}/).pop() ?? '') : '';
  const title = isStreaming ? 'Building your site' : parts.length > 0 ? 'Worked on your site' : 'Thought it through';

  return (
    <div className="my-2.5">
      <div
        className="group/wb flex items-center gap-2 py-1 px-1.5 -mx-1.5 rounded-md cursor-pointer select-none transition-colors hover:bg-white/[0.025]"
        onClick={() => setOpen(!open)}
        title={open ? 'Hide the steps' : 'Show the steps'}
      >
        {isStreaming ? (
          <DuneaSpinner size={14} className="text-accent flex-shrink-0" />
        ) : (
          <span className="i-ph:check-circle text-[15px] text-accent/80 flex-shrink-0" />
        )}
        <span className={`text-[14px] font-medium flex-shrink-0 ${isStreaming ? 'text-primary' : 'text-secondary'}`}>
          {title}
        </span>
        {summary && <span className="text-[12.5px] text-faint truncate min-w-0">· {summary}</span>}
        <span
          className={`i-ph:caret-down text-[11px] text-faint flex-shrink-0 ml-auto transition-transform ${
            open ? '' : '-rotate-90'
          }`}
        />
      </div>

      {open && (
        <div className="mt-2 ml-[1px] rounded-xl border border-hairline bg-surface/40 px-3.5 py-3">
          {showAll ? (
            <div className="flex flex-col">{blocks.map(renderBlock)}</div>
          ) : rows.length > 0 ? (
            <ol className="m-0 list-none p-0">
              {isStreaming && hidden > 0 && (
                <li className="pb-2 pl-[32px] text-[12px] text-faint">{hidden} earlier steps</li>
              )}
              {rows.map((row, i) => (
                <TimelineItem key={row.key} row={row} isLast={i === rows.length - 1} />
              ))}
            </ol>
          ) : (
            <div className="flex items-center gap-2 text-[13px] text-muted">
              {isStreaming && <DuneaSpinner size={12} className="text-accent" />}
              {isStreaming ? 'Getting started…' : 'Planned the work'}
            </div>
          )}

          {now && (
            <div className="mt-2.5 flex items-start gap-2 border-t border-hairline pt-2.5 text-[12.5px] leading-[1.5] text-muted">
              <span className="mt-[6px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent ui-pulsing" />
              <span className="min-w-0">{now}</span>
            </div>
          )}

          {total > rows.length && (
            <button
              type="button"
              onClick={() => setShowAll(!showAll)}
              className="mt-2.5 ml-[32px] flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer text-[12px] text-faint hover:text-secondary transition-colors"
            >
              <span className={`${showAll ? 'i-ph:arrows-in-simple' : 'i-ph:list-bullets'} text-[12px]`} />
              {showAll ? 'Show the highlights' : `Show all ${total} ${total === 1 ? 'step' : 'steps'}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
